import Phaser from 'phaser';
import type { Card, Die, GameState } from '../types';
import { GameController } from '../game/GameController';
import {
  newGame,
  defaultStartingBag,
  tieBreakerNeeded,
  lowestTiedDice,
} from '../game/reducer';
import { cardPools } from '../data/cardPools';
import { PyramidView } from '../ui/PyramidView';
import { HandView } from '../ui/HandView';
import { StatusBar } from '../ui/StatusBar';
import type { MenuStartData } from './MenuScene';

export class GameScene extends Phaser.Scene {
  controller!: GameController;
  pyramidView!: PyramidView;
  handView!: HandView;
  statusBar!: StatusBar;
  pendingTieBreakerCardId: string | null = null;

  constructor() {
    super('Game');
  }

  init(data: MenuStartData): void {
    const initial = newGame({
      seed: data.seed,
      cardPools,
      startingBag: defaultStartingBag(),
    });
    this.controller = new GameController(initial);
  }

  create(): void {
    const { width, height } = this.scale;

    const statusH = 56;
    const handH = 130;
    const pyramidTop = 12;
    const pyramidH = height - statusH - handH - pyramidTop - 12;

    this.pyramidView = new PyramidView(this, this.controller.state, {
      pyramidTop,
      pyramidHeight: pyramidH,
      onCardTap: (id) => this.handleCardTap(id),
    });

    this.handView = new HandView(this, {
      x: 0,
      y: height - statusH - handH,
      width,
      height: handH,
      onDieTap: (die) => this.handleDieTap(die),
      onAdjust: (die, delta) =>
        this.controller.dispatch({ type: 'adjustDie', dieId: die.id, delta }),
      onReroll: (die) => this.controller.dispatch({ type: 'rerollDie', dieId: die.id }),
      onUseToken: (token, die) =>
        this.controller.dispatch({ type: 'useToken', token, dieId: die.id }),
    });

    this.statusBar = new StatusBar(this, {
      x: 0,
      y: height - statusH,
      width,
      height: statusH,
      onRoll: () => this.handleRoll(),
      onFinishPlacing: () => this.controller.dispatch({ type: 'finishPlacing' }),
      onEndTurn: () => this.controller.dispatch({ type: 'endTurn' }),
      onMenu: () => this.scene.start('Menu'),
    });

    this.controller.on('change', (state: GameState) => this.refreshAll(state));
    this.refreshAll(this.controller.state);

    // Watch for terminal state
    this.controller.on('change', (state: GameState) => {
      if (state.phase === 'Win' || state.phase === 'Loss') {
        this.time.delayedCall(450, () => {
          this.scene.start('GameOver', { phase: state.phase });
        });
      }
    });
  }

  private handleRoll(): void {
    const state = this.controller.state;
    if (state.phase === 'Drawing') {
      this.controller.dispatch({ type: 'startTurn' });
      // After drawing, immediately roll for v1 simplicity
      this.controller.dispatch({ type: 'roll' });
    } else if (state.phase === 'Rolling') {
      this.controller.dispatch({ type: 'roll' });
    }
  }

  private handleDieTap(die: Die): void {
    const state = this.controller.state;
    if (state.phase !== 'AdjustingPerks' && state.phase !== 'Placing') return;
    const current = this.handView.selectedDieId;
    this.handView.setSelectedDie(current === die.id ? null : die.id);
    this.refreshAll(this.controller.state);
  }

  private handleCardTap(cardId: string): void {
    const state = this.controller.state;
    const card = state.pyramid.find((c) => c.id === cardId);
    if (!card) return;

    // If a die is selected and we're placing, place on this card.
    if (
      this.handView.selectedDieId &&
      (state.phase === 'AdjustingPerks' || state.phase === 'Placing')
    ) {
      const dieId = this.handView.selectedDieId;
      this.controller.dispatch({ type: 'placeDie', dieId, cardId });
      this.handView.setSelectedDie(null);
      this.refreshAll(this.controller.state);
      return;
    }

    // In Resolving, tap acquires.
    if (state.phase === 'Resolving') {
      if (tieBreakerNeeded(card)) {
        this.promptTieBreaker(card);
      } else {
        this.controller.dispatch({ type: 'acquireCard', cardId });
      }
      return;
    }

    // Otherwise, zoom in
    this.scene.launch('CardZoom', { cardId });
    this.scene.pause('Game');
  }

  private promptTieBreaker(card: Card): void {
    this.scene.launch('CardZoom', {
      cardId: card.id,
      tieBreakerDice: lowestTiedDice(card),
      onChoose: (dieId: string) => {
        this.scene.resume('Game');
        this.controller.dispatch({
          type: 'acquireCard',
          cardId: card.id,
          tieBreakerDieId: dieId,
        });
      },
    });
    this.scene.pause('Game');
  }

  private refreshAll(state: GameState): void {
    // If selected die was placed, clear selection
    if (
      this.handView.selectedDieId &&
      !state.hand.some((d) => d.id === this.handView.selectedDieId)
    ) {
      this.handView.setSelectedDie(null);
    }
    this.pyramidView.refresh(state);
    this.handView.refresh(state);
    this.statusBar.refresh(state);

    // Phase hint banner (top edge)
    this.drawPhaseBanner(state);
  }

  phaseBannerText: Phaser.GameObjects.Text | null = null;
  drawPhaseBanner(state: GameState): void {
    const txt = this.phaseHint(state);
    if (!this.phaseBannerText) {
      this.phaseBannerText = this.add
        .text(this.scale.width / 2, 2, txt, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '11px',
          color: '#fbbf24',
        })
        .setOrigin(0.5, 0)
        .setDepth(1000);
    } else {
      this.phaseBannerText.setText(txt);
    }
  }

  phaseHint(state: GameState): string {
    switch (state.phase) {
      case 'Drawing':
        return 'Tap "Draw" to start the turn';
      case 'Rolling':
        return 'Roll the dice';
      case 'AdjustingPerks':
        return 'Apply ±1 / reroll perks, then place dice';
      case 'Placing':
        return state.hand.length === 0
          ? 'All dice placed — tap "Done placing"'
          : 'Tap a die, then tap a card to place it';
      case 'Resolving':
        return state.lastAcquired
          ? 'Tap any glowing card to acquire (or End Turn)'
          : 'Tap a glowing bottom-row card to start your route';
      case 'Win':
        return 'You reached the apex!';
      case 'Loss':
        return 'Bag is empty — game over.';
      default:
        return '';
    }
  }
}
